import { Component, computed, inject, signal, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslatePipe } from '@ngx-translate/core';
import { JjamesButtonComponent, ResponsiveBaseComponent } from 'shared-lib';
import { MyLicensePlatesService } from './my-license-plates.service';
import { JkuUserService } from '../jku-webshop/jku-user.service';
import { ParkerLicenseDto } from '../../api/gen/model/parkerLicenseDto';
import { PlateComponent, RegionSeparator, findPlateCountry, normalizePlate } from 'shared-lib';
/** A plate row prepared for {@link PlateComponent}. */
interface PlateRow {
  licensePlateId: string | undefined;
  fullNumber: string;
  countryCode: string;
  euBand: string;
  region: string;
  regionSeparator: RegionSeparator;
  /** The original DTO, so the remove action keeps working off the API shape. */
  source: ParkerLicenseDto;
}

/**
 * "My License Plates" — lists the plates on the authenticated parker's account and
 * lets them add (→ register sub-route) or remove one. Backed by /parker/me/license-plates.
 */
@Component({
  selector: 'app-license-plates',
  imports: [CommonModule, MatIconModule, MatProgressSpinnerModule, JjamesButtonComponent, PlateComponent,
    TranslatePipe],
  templateUrl: './license-plates.component.html',
  styleUrl: './license-plates.component.scss'
})
export class LicensePlatesComponent extends ResponsiveBaseComponent implements OnInit {
  private readonly api = inject(MyLicensePlatesService);
  private readonly router = inject(Router);
  private readonly jkuUser = inject(JkuUserService);

  protected readonly plates = signal<ParkerLicenseDto[]>([]);
  protected readonly isLoading = signal(false);
  /**
   * The error as a translation *key*, resolved by the template's pipe rather than by
   * `translate.instant()`. Resolved text is frozen in the language it was created in, so an
   * error already on screen would survive the DE/EN switch untranslated.
   */
  protected readonly errorKey = signal<string | null>(null);
  /**
   * Backend-supplied reason, shown verbatim when present (KAN-456).
   *
   * The removal error used to be discarded entirely — the error callback took no parameter — so an
   * "this plate is currently inside a parking site" refusal rendered as the generic
   * "please try again" copy. That is the single most service-centre-call-generating thing this screen
   * could do. There is no machine-readable error code on the API yet (the OpenAPI document declares
   * only 200 for every plate operation), so the backend's own message is the best signal available.
   */
  protected readonly errorText = signal<string | null>(null);
  protected readonly removingId = signal<string | null>(null);

  /**
   * Plates prepared for rendering. The district code comes from the stored `state`; plates
   * registered before it was captured simply render undivided rather than being split by guess.
   */
  protected readonly plateRows = computed<PlateRow[]>(() =>
    this.plates().map(link => {
      const countryCode = link.licensePlate?.countryCode?.trim().toUpperCase() || 'AT';
      const country = findPlateCountry(countryCode);
      return {
        licensePlateId: link.licensePlate?.id,
        fullNumber: normalizePlate(link.licensePlate?.fullNumber),
        countryCode,
        euBand: country?.euBand ?? countryCode,
        region: link.licensePlate?.state?.trim().toUpperCase() || '',
        regionSeparator: country?.regionSeparator ?? 'none',
        source: link
      };
    }));

  ngOnInit(): void {
    this.load();
  }

  IsJKUUser(): boolean {
    return this.jkuUser.isJkuSso();
  }
  protected load(): void {
    this.isLoading.set(true);
    this.errorKey.set(null);
    this.api.list().subscribe({
      next: (plates) => {
        this.plates.set(plates ?? []);
        this.isLoading.set(false);
      },
      error: () => {
        this.errorKey.set('MY_PLATES.ERROR_LOAD');
        this.isLoading.set(false);
      }
    });
  }

  protected addPlate(): void {
    // JKU SSO users register through the webshop instead: it is the flow that carries their
    // student/employee classification onto the plate. The generic form here cannot do that.
    if (this.jkuUser.isJkuSso()) {
      this.router.navigate(['/JKU-webshop']);
      return;
    }
    this.router.navigate(['/license-plates/register']);
  }

  /**
   * Pulls a human-readable refusal out of an HttpErrorResponse.
   *
   * `ToActionResult` serialises MadResult errors inconsistently — sometimes a plain string, sometimes
   * an object carrying Title/Detail — so both shapes are handled. Returns null for transport-level
   * failures (status 0, 5xx with no body), where the generic message is the honest one.
   */
  private extractReason(err: unknown): string | null {
    const body = (err as { error?: unknown })?.error;

    if (typeof body === 'string' && body.trim().length > 0) {
      return body.trim();
    }

    if (body && typeof body === 'object') {
      const { detail, title } = body as { detail?: unknown; title?: unknown };
      const text = [detail, title].find(v => typeof v === 'string' && v.trim().length > 0);
      if (typeof text === 'string') {
        return text.trim();
      }
    }

    return null;
  }

  protected remove(plate: ParkerLicenseDto): void {
    const id = plate.licensePlate?.id;
    if (!id || this.removingId()) {
      return;
    }
    this.removingId.set(id);
    this.errorKey.set(null);
    this.errorText.set(null);
    this.api.remove(id).subscribe({
      next: () => {
        this.removingId.set(null);
        this.load();
      },
      error: (err: unknown) => {
        this.removingId.set(null);
        const reason = this.extractReason(err);
        if (reason) {
          this.errorText.set(reason);
        } else {
          this.errorKey.set('MY_PLATES.ERROR_REMOVE');
        }
      }
    });
  }
}
